import api from '@/services/api';
import { DEALER_CONFIG_FETCH_TIMEOUT_MS } from '../types/dealerConfig.types';
import type {
    ApplyDealerConfigCatalogPayload,
    ApplyDealerConfigCatalogResult,
    AttachDealerConfigSettingPayload,
    DealerConfigSetting,
    DealerConfigSettingsListParams,
    UpdateDealerConfigSettingByIdPayload,
    UpdateDealerConfigSettingPayload,
} from '../types/dealerConfig.types';
import type { DealerConfigOption } from '../types/dealerConfigOption.types';

const dealerConfigSettingsPath = (dealerId: string): string => `/dealers/${dealerId}/config-settings`;

/**
 * Raw per-dealer config setting shape as serialized by the backend. Laravel
 * serializes the `configOption()` BelongsTo relation as snake_case
 * (`config_option`) — the common-package `ConfigSetting` model behaves the
 * same way SMR's `ProductConfigSetting` does (Eloquent's default
 * `$snakeAttributes = true`) — while the frontend type contract
 * (`DealerConfigSetting.configOption`) and every consuming component
 * (`DealerConfigSettingDataTable`) expect camelCase. Normalize at this
 * boundary, mirroring `products/services/productTransform.ts`'s
 * `normalizeConfigSetting`.
 */
type RawDealerConfigSetting = Omit<DealerConfigSetting, 'configOption'> & {
    configOption?: DealerConfigOption | null;
    config_option?: DealerConfigOption | null;
};

/**
 * Rename `config_option` -> `configOption` (preferring an already-camelCase
 * value if present) so every caller receives the shape `DealerConfigSetting`
 * promises.
 */
function normalizeDealerConfigSetting(raw: RawDealerConfigSetting): DealerConfigSetting {
    const { config_option: configOptionSnake, configOption: configOptionCamel, ...rest } = raw;
    return {
        ...rest,
        configOption: configOptionCamel ?? configOptionSnake ?? null,
    };
}

function normalizeDealerConfigSettings(raw: RawDealerConfigSetting[]): DealerConfigSetting[] {
    return raw.map(normalizeDealerConfigSetting);
}

const dealerPath = (dealerId: string): string => `/dealers/${dealerId}`;

/**
 * Read one dealer's config settings through the OPT-IN dealer BREAD read (#789):
 * `GET /dealers/{id}?with=effective_config_settings`, under `permission:dealer.read`.
 *
 * Deliberately NOT `fetchDealerConfigSettings` above, which calls
 * GET /dealers/{id}/config-settings and requires the ADMIN-ONLY
 * `dealer.config-setting.browse` (rbac-service DealerConfigPermissionsSeeder:17,22).
 * A quoting user cannot use that endpoint; this one they can.
 *
 * Lives in THIS file rather than `dealers.ts` because the response's nested
 * relation serialises as snake_case `config_option` and this file already owns
 * `normalizeDealerConfigSettings` for exactly that. Duplicating the normaliser in
 * dealers.ts would create a second place for it to drift.
 *
 * `suppressGlobalForbiddenToast` is set because this runs as an AMBIENT prefetch
 * with no user-initiated action behind it — a 403 must degrade silently to the
 * default-OFF flag (plan D6), not surface a toast the user cannot act on.
 *
 * Returns [] when the key is absent from the payload, which the resolver reads as
 * "not configured" -> false.
 *
 * `?with=effective_config_settings` (7.1.6 Slice 1): the backend resolves this
 * dealer's settings against its owning group's, own value winning per key.
 *
 * `timeout` is set PER REQUEST — the shared Axios client has none, and a
 * client-wide default would change behaviour for every caller in the app.
 */
export const fetchDealerConfigSettingsViaShow = async (
    dealerId: string,
): Promise<DealerConfigSetting[]> => {
    const response = await api.get<{
        success: boolean;
        data?: { effective_config_settings?: RawDealerConfigSetting[] };
    }>(dealerPath(dealerId), {
        params: { with: 'effective_config_settings' },
        suppressGlobalForbiddenToast: true,
        timeout: DEALER_CONFIG_FETCH_TIMEOUT_MS,
    });

    if (!response.data.success) {
        throw new Error('Failed to fetch dealer config settings');
    }

    return normalizeDealerConfigSettings(response.data.data?.effective_config_settings ?? []);
};

// ---- Per-dealer settings ----

/**
 * `GET /dealers/{dealer}/config-settings` — NOT a paginated envelope (verified
 * against `DealerConfigSettingController::configSettings()`); normalises
 * `{ data: [...] }` directly. Optional `limit` (1-100, backend defaults 100).
 */
export const fetchDealerConfigSettings = async (
    dealerId: string,
    params: DealerConfigSettingsListParams = {},
): Promise<DealerConfigSetting[]> => {
    const response = await api.get(dealerConfigSettingsPath(dealerId), { params });
    const raw = response.data as { data?: RawDealerConfigSetting[] };
    return normalizeDealerConfigSettings(raw.data ?? []);
};

export const attachDealerConfigSetting = async (
    dealerId: string,
    settings: AttachDealerConfigSettingPayload[],
): Promise<DealerConfigSetting[]> => {
    const response = await api.post<{ data: RawDealerConfigSetting[] }>(
        dealerConfigSettingsPath(dealerId),
        { settings },
    );
    return normalizeDealerConfigSettings(response.data.data);
};

// Keys are user-controlled (the null-option add path) and may contain
// characters that need URL-encoding — encode the path segment to keep the
// URL well-formed (mirrors smr's mutations).
export const updateDealerConfigSetting = async (
    dealerId: string,
    key: string,
    payload: UpdateDealerConfigSettingPayload,
): Promise<DealerConfigSetting> => {
    const response = await api.put<{ data: RawDealerConfigSetting }>(
        `${dealerConfigSettingsPath(dealerId)}/${encodeURIComponent(key)}`,
        payload,
    );
    return normalizeDealerConfigSetting(response.data.data);
};

export const updateDealerConfigSettingById = async (
    dealerId: string,
    settingId: string,
    payload: UpdateDealerConfigSettingByIdPayload,
): Promise<DealerConfigSetting> => {
    const response = await api.put<{ data: RawDealerConfigSetting }>(
        `${dealerConfigSettingsPath(dealerId)}/by-id/${settingId}`,
        payload,
    );
    return normalizeDealerConfigSetting(response.data.data);
};

export const deleteDealerConfigSetting = async (dealerId: string, key: string): Promise<void> => {
    await api.delete(`${dealerConfigSettingsPath(dealerId)}/${encodeURIComponent(key)}`);
};

// 204 No Content — verified against DealerConfigSettingController::deleteById().
export const deleteDealerConfigSettingById = async (dealerId: string, settingId: string): Promise<void> => {
    await api.delete(`${dealerConfigSettingsPath(dealerId)}/by-id/${settingId}`);
};

export const applyDealerConfigCatalog = async (
    dealerId: string,
    payload: ApplyDealerConfigCatalogPayload,
): Promise<ApplyDealerConfigCatalogResult> => {
    const response = await api.post<{
        data: Omit<ApplyDealerConfigCatalogResult, 'data'> & { data: RawDealerConfigSetting[] };
    }>(`${dealerConfigSettingsPath(dealerId)}/apply-catalog`, payload);
    const result = response.data.data;
    return { ...result, data: normalizeDealerConfigSettings(result.data) };
};
